/**
 * Upload Helper
 * Automatically chooses between TUS (resumable) and normal upload based on file size
 */

import { uploadWithTUS } from './tusUpload';
import { sttApi } from '../api/sttApi';
import { getMaxFileSize, getTusThresholdBytes } from '../config';

const pollTusUploadStatus = async (uploadToken, timeoutMs = 15000, intervalMs = 2000) => {
    const startTime = Date.now();
    while (Date.now() - startTime < timeoutMs) {
        try {
            const status = await sttApi.getTusUploadStatus(uploadToken);
            if (status?.status === 'failed') {
                const message = status?.error_message || 'Upload failed due to insufficient credits.';
                throw new Error(message);
            }
            if (status?.status === 'completed') {
                return;
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                // Session not yet visible; continue polling briefly
            } else if (error?.message) {
                throw error;
            }
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
};

/**
 * Upload file - automatically chooses TUS for large files, normal upload for small files
 * @param {File} file - File to upload
 * @param {number} apiKeyId - API key ID (JWT auth)
 * @param {Function} onProgress - Progress callback (percent, uploaded, total)
 * @param {boolean} wordTimestamps - Whether to include word-level timestamps (default: false)
 * @param {boolean} enableDiarization - Whether to enable speaker diarization (default: false)
 * @returns {Promise<Object>} - Job response or TUS file ID
 */
export const uploadFile = async (file, apiKeyId, onProgress = null, wordTimestamps = false, enableDiarization = false) => {
    const maxFileSize = getMaxFileSize();
    const tusThresholdBytes = getTusThresholdBytes();
    if (file.size > tusThresholdBytes) {
        // Large file - use TUS resumable upload
        console.log(`File size ${file.size} exceeds ${tusThresholdBytes}, using TUS upload`);

        if (!apiKeyId) {
            throw new Error("Please select an API key for TUS uploads.");
        }
        const tokenResponse = await sttApi.createTusUploadTokenUser(apiKeyId, 5);
        const uploadToken = tokenResponse.upload_token;

        // Upload with TUS
        const tusFileId = await uploadWithTUS(
            file,
            uploadToken,
            onProgress,
            undefined
        );

        // Short polling window to surface failed status immediately
        await pollTusUploadStatus(uploadToken);

        // Return TUS file ID (job will be created automatically via webhook)
        return {
            tus_file_id: tusFileId,
            upload_token: uploadToken,
            method: 'tus'
        };
    } else {
        // Small file - use normal upload
        console.log(`File size ${file.size} <= ${tusThresholdBytes}, using normal upload`);

        if (!apiKeyId) {
            throw new Error("Please select an API key for normal uploads.");
        }
        const jobResponse = await sttApi.createTranscriptionJob(file, apiKeyId, onProgress, wordTimestamps, enableDiarization);
        return {
            ...jobResponse,
            method: 'normal'
        };
    }
};

